import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import DownloadIcon from "@mui/icons-material/SaveAltRounded";
import { IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import FileIcon from "@entrilia/shared/components/FileIcon";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import adminApi from "../../../../../api/adminApi";
import type { WorkflowFileInfo } from "../../../../../api/types/workflowTypes";

const getDownloadInfo = withErrorHandling(adminApi.getWorkflowDocumentFileDownloadInfo);

interface Props {
  workflowId: string;
  attachment: WorkflowFileInfo;
  canDelete: boolean;
  onOpenPreview: (attachment: WorkflowFileInfo) => void;
  onDeleteRequested: (attachment: WorkflowFileInfo) => void;
}

const WorkflowDocumentsSectionAttachmentRow = ({
  workflowId,
  attachment,
  canDelete,
  onOpenPreview,
  onDeleteRequested,
}: Props) => {
  const [downloading, setDownloading] = useState(false);
  const { sendNotificationError } = useNotificationContext();

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setDownloading(true);
    const [downloadInfo, error] = await getDownloadInfo(workflowId, attachment.fileCatalogId);
    setDownloading(false);
    if (error) {
      logError(error, "[WorkflowDocumentsSectionAttachmentRow] getWorkflowDocumentFileDownloadInfo");
      sendNotificationError(error.message);
      return;
    }
    await downloadFileFromUrl(downloadInfo.downloadUrl);
  };

  const isDeletable = attachment.isTriggerFile === false && canDelete;

  const deleteTooltip =
    attachment.isTriggerFile === true
      ? "This file triggered the workflow and cannot be deleted"
      : attachment.isTriggerFile == null
        ? "This file is from a legacy workflow and cannot be deleted"
        : !canDelete
          ? "Delete is unavailable while the workflow is active"
          : "";

  return (
    <Paper
      variant="outlined"
      onClick={() => onOpenPreview(attachment)}
      sx={(t) => ({ cursor: "pointer", "&:hover": { borderColor: t.palette.primary.main } })}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", px: 1.5, py: 1 }}>
        <FileIcon fileExtension={getFileExtension(attachment.fileName)} />
        <Typography noWrap sx={{ flex: 1 }}>
          {attachment.fileName}
        </Typography>
        <IconButton loading={downloading} onClick={handleDownload}>
          <DownloadIcon fontSize="small" />
        </IconButton>
        <Tooltip title={deleteTooltip}>
          <span onClick={(e) => e.stopPropagation()}>
            <IconButton
              color="error"
              disabled={!isDeletable}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteRequested(attachment);
              }}
            >
              <DeleteOutlineRounded />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
};

export default WorkflowDocumentsSectionAttachmentRow;
